import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Check, X, Clock, Plus } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AttendanceSystem = () => {
  const { t } = useTranslation('attendancesystem');
  const { api, user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [students, setStudents] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Form state with current date and time defaults
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStream, setSelectedStream] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(
    new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit'
    })
  );
  const [attendance, setAttendance] = useState({});
  const [remarks, setRemarks] = useState({});

  const navigate = useNavigate()

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesRes, sessionRes] = await Promise.all([
          api.get(`/api/v1/admin/schools/${user.school_reg}/classes`),
          api.get(`/api/v1/attendance/schools/${user.school_id}/sessions/active`)
        ]);
        setClasses(classesRes.data);
        setActiveSession(sessionRes.data);
      } catch (err) {
        setError(t('failedToLoadInitialData'));
        toast.error(t('failedToLoadInitialData'), {
          duration: 3000,
          position: 'top-center',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  // Fetch ALL students with pagination handling
  const fetchAllStudents = async () => {
    try {
      // First request to get total pages
      const initialResponse = await api.get(`/api/v1/admin/schools/${user.school_reg}/students`, {
        params: { page: 1, page_size: 50 }
      });
      
      const totalPages = initialResponse.data.total_pages;
      let allStudents = [...initialResponse.data.items];
      
      // Fetch remaining pages if needed
      if (totalPages > 1) {
        const remainingRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          remainingRequests.push(
            api.get(`/api/v1/admin/schools/${user.school_reg}/students`, {
              params: { page, page_size: 50 }
            })
          );
        }
        
        const responses = await Promise.all(remainingRequests);
        responses.forEach(response => {
          allStudents = [...allStudents, ...response.data.items];
        });
      }
      
      return allStudents;
    } catch (err) {
      throw new Error(t('failedToLoadStudents'));
    }
  };

  // Fetch students and set default attendance
  useEffect(() => {
    const getStudentsForClassAndStream = async () => {
      if (!selectedClass || !selectedStream) {
        setStudents([]);
        return;
      }
      
      try {
        const allStudents = await fetchAllStudents();
        
        const filteredStudents = allStudents.filter(
          student => student.class_id === parseInt(selectedClass) && 
                     student.stream_id === parseInt(selectedStream)
        );
        
        setStudents(filteredStudents);
        
        // Initialize attendance as 'present' and empty remarks for new student list
        const initialAttendance = {};
        const initialRemarks = {};
        filteredStudents.forEach(student => {
          initialAttendance[student.id] = 'present';
          initialRemarks[student.id] = '';
        });
        
        setAttendance(initialAttendance);
        setRemarks(initialRemarks);
      } catch (err) {
        setError(t('failedToLoadStudents'));
        toast.error(t('failedToLoadStudents'), {
          duration: 3000,
          position: 'top-center',
        });
      }
    };
    
    getStudentsForClassAndStream();
  }, [selectedClass, selectedStream, t]);

  // Update streams when class is selected
  useEffect(() => {
    if (!selectedClass) {
      setStreams([]);
      setSelectedStream('');
      return;
    }
    const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
    setStreams(selectedClassData?.streams || []);
    setSelectedStream('');
  }, [selectedClass, classes]);

  // Check if selected date falls within active session
  const isDateValid = () => {
    if (!activeSession || !selectedDate) return false;
    const selectedDateObj = new Date(selectedDate);
    const startDate = new Date(activeSession.start_date);
    const endDate = new Date(activeSession.end_date);
    
    // Check if date is within range
    if (selectedDateObj < startDate || selectedDateObj > endDate) return false;
    
    // Check if day of week is allowed
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const dayOfWeek = days[selectedDateObj.getDay()];
    return activeSession.weekdays.includes(dayOfWeek);
  };

  // Check if selected time falls within session time
  const isTimeValid = () => {
    if (!activeSession || !selectedTime) return false;
    const [selectedHour, selectedMinute] = selectedTime.split(':').map(Number);
    const [startHour, startMinute] = activeSession.start_time.split(':').map(Number);
    const [endHour, endMinute] = activeSession.end_time.split(':').map(Number);

    const selectedTimeMinutes = selectedHour * 60 + selectedMinute;
    const startTimeMinutes = startHour * 60 + startMinute;
    const endTimeMinutes = endHour * 60 + endMinute;

    return selectedTimeMinutes >= startTimeMinutes && selectedTimeMinutes <= endTimeMinutes;
  };

  const isFormValid = () => {
    return (
      activeSession &&
      selectedClass &&
      selectedStream &&
      selectedDate &&
      selectedTime &&
      isDateValid() &&
      isTimeValid() &&
      students.length > 0 &&
      students.every(student => attendance[student.id])
    );
  };

  const handleSubmit = async () => {
    if (!activeSession) {
      setError(t('noActiveSession'));
      toast.error(t('noActiveSession'), {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const loadingToast = toast.loading(t('submittingAttendance'), {
        position: 'top-center'
      });

      const promises = students.map(student => 
        api.post(`/api/v1/attendance/schools/${user.school_id}/attendance/${student.id}`, {
          student_id: student.id,
          class_id: parseInt(selectedClass),
          stream_id: parseInt(selectedStream),
          session_id: activeSession.id,
          school_id: user.school_id,
          date: selectedDate,
          time: `${selectedTime}:00Z`,
          status: attendance[student.id],
          remarks: remarks[student.id]
        })
      );

      await Promise.all(promises);
      
      toast.dismiss(loadingToast);
      toast.success(t('attendanceSubmittedSuccessfully'), {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#10B981',
          color: '#fff',
        },
      });
      
      // Reset form
      setAttendance({});
      setRemarks({});
      setSelectedDate('');
      setSelectedTime('');
      
    } catch (err) {
      setError(t('failedToSubmitAttendance'));
      toast.error(t('failedToSubmitAttendance'), {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  function handleAddSession(){
    navigate('/session-management');
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!activeSession) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg flex items-center space-x-4">
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg flex-grow">
          {t('noActiveSessionFound')}
        </div>
        <button 
          onClick={handleAddSession}
          className="bg-[#0E5252] text-white px-4 py-2 rounded-lg shadow flex items-center transition-all duration-200 text-sm md:text-base"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('addSession')}
        </button>
      </div>
    );
  }

  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }}
      />
      <div className="p-2 sm:p-4 md:p-6 bg-white rounded-lg shadow-lg">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{t('markAttendance')}</h1>
        
        {/* Session Info */}
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg text-sm sm:text-base">
          <h2 className="font-semibold text-blue-700">{t('activeSession')}: {activeSession?.name}</h2>
          <p className="text-blue-600">
            {t('period')}: {activeSession?.start_date} {t('to')} {activeSession?.end_date}
          </p>
          <p className="text-blue-600">
            {t('time')}: {activeSession?.start_time} {t('to')} {activeSession?.end_time}
          </p>
          <p className="text-blue-600">
            {t('days')}: {activeSession?.weekdays?.join(', ')}
          </p>
        </div>
        
        {/* Filters - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <select
            className="p-2 border rounded-lg text-sm sm:text-base w-full"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">{t('selectClass')}</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>{cls.name}</option>
            ))}
          </select>

          <select
            className="p-2 border rounded-lg text-sm sm:text-base w-full"
            value={selectedStream}
            onChange={(e) => setSelectedStream(e.target.value)}
            disabled={!selectedClass}
          >
            <option value="">{t('selectStream')}</option>
            {streams.map(stream => (
              <option key={stream.id} value={stream.id}>{stream.name}</option>
            ))}
          </select>

          <div className="flex flex-col">
            <input
              type="date"
              className={`p-2 border rounded-lg text-sm sm:text-base w-full ${
                !isDateValid() && selectedDate ? 'border-red-500' : ''
              }`}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={activeSession?.start_date}
              max={activeSession?.end_date}
            />
            {!isDateValid() && selectedDate && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">
                {t('invalidDateOrDay')}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <input
              type="time"
              className={`p-2 border rounded-lg text-sm sm:text-base w-full ${
                !isTimeValid() && selectedTime ? 'border-red-500' : ''
              }`}
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
            />
            {!isTimeValid() && selectedTime && (
              <p className="text-red-500 text-xs sm:text-sm mt-1">
                {t('timeMustBeBetween')} {activeSession?.start_time} {t('and')} {activeSession?.end_time}
              </p>
            )}
          </div>
        </div>

        {/* Students Table - Responsive with horizontal scroll */}
        {students.length > 0 && (
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <div className="min-w-full inline-block align-middle">
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">{t('admissionNo')}</th>
                      <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">{t('name')}</th>
                      <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">{t('gender')}</th>
                      <th className="p-2 sm:p-4 text-center text-xs sm:text-sm font-medium text-gray-700">{t('status')}</th>
                      <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-medium text-gray-700">{t('remarks')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {students.map(student => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                          {student.admission_number}
                        </td>
                        <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                          {student.name}
                        </td>
                        <td className="p-2 sm:p-4 text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                          {student.gender}
                        </td>
                        <td className="p-2 sm:p-4">
                          <div className="flex justify-center gap-1 sm:gap-2">
                            {[
                              { value: 'present', icon: Check, label: t('present'), colors: 'bg-green-100 text-green-800 border-green-200' },
                              { value: 'absent', icon: X, label: t('absent'), colors: 'bg-red-100 text-red-800 border-red-200' },
                              { value: 'late', icon: Clock, label: t('late'), colors: 'bg-yellow-100 text-yellow-800 border-yellow-200' }
                            ].map(({ value, icon: Icon, label, colors }) => (
                              <label
                                key={value}
                                className="relative cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name={`attendance-${student.id}`}
                                  value={value}
                                  checked={attendance[student.id] === value}
                                  onChange={(e) => setAttendance({
                                    ...attendance,
                                    [student.id]: e.target.value
                                  })}
                                  className="sr-only"
                                />
                                <div className={`
                                  px-3 py-1 text-xs sm:text-sm border rounded-full capitalize whitespace-nowrap
                                  transition-colors duration-200 flex items-center gap-1
                                  ${attendance[student.id] === value 
                                    ? colors
                                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                  }
                                `}>
                                  <Icon size={14} className="sm:w-4 sm:h-4" />
                                  {label}
                                </div>
                              </label>
                            ))}
                          </div>
                        </td>
                        <td className="p-2 sm:p-4">
                          <input
                            type="text"
                            className="w-full p-1.5 sm:p-2 border rounded-lg text-xs sm:text-sm"
                            placeholder={t('addRemarks')}
                            value={remarks[student.id] || ''}
                            onChange={(e) => setRemarks({
                              ...remarks,
                              [student.id]: e.target.value
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-4 sm:mt-6 flex justify-end">
          <button
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            onClick={handleSubmit}
            disabled={submitting || !isFormValid()}
          >
            {submitting ? t('submitting') : t('submitAttendance')}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 sm:p-4 bg-red-50 text-red-700 text-sm sm:text-base rounded-lg">
            {error}
          </div>
        )}
      </div>
    </>
  );
};

export default AttendanceSystem;